"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

export const LOADING_MESSAGES = [
	"Loading the best results for you",
	"Connecting to our servers",
	"Just a moment, please",
	"Updating your information",
	"Processing your request",
	"Almost there",
	"Fetching the latest data",
	"Preparing everything for you",
	"Hang tight, we're on it",
	"Crunching the numbers",
	"Putting the pieces together",
	"Working behind the scenes",
	"Syncing with the latest updates",
	"Getting things ready for you",
	"Optimizing your experience",
	"Loading fresh content",
	"Securing your connection",
	"Verifying everything looks good",
	"Tidying up the results",
	"Polishing the final details",
	"Thanks for your patience",
	"This won't take long",
	"Bringing the data home",
	"Wrapping things up",
	"Finalizing the details",
];

const VARIANT_CLASS = {
	caption: "text-xs",
	body2: "text-sm",
	body1: "text-base",
} as const;

const ALIGN_CLASS = {
	left: "text-left",
	center: "text-center",
	right: "text-right",
} as const;

interface RotatingLoadingTextProps {
	messages?: string[];
	intervalMs?: number;
	variant?: "caption" | "body2" | "body1";
	align?: "left" | "center" | "right";
	/** Any CSS color. Defaults to the muted foreground token. */
	color?: string;
}

const RotatingLoadingText = ({
	messages = LOADING_MESSAGES,
	intervalMs = 10000,
	variant = "caption",
	align = "center",
	color,
}: RotatingLoadingTextProps) => {
	const [index, setIndex] = useState(() =>
		Math.floor(Math.random() * messages.length),
	);
	const [show, setShow] = useState(true);
	const [dots, setDots] = useState(0);

	useEffect(() => {
		if (messages.length <= 1) return;

		const cycle = setInterval(() => {
			setShow(false);
			setTimeout(() => {
				setIndex((prev) => {
					if (messages.length <= 1) return 0;
					let nextIndex = Math.floor(Math.random() * messages.length);
					while (nextIndex === prev) {
						nextIndex = Math.floor(Math.random() * messages.length);
					}
					return nextIndex;
				});
				setShow(true);
			}, 250);
		}, intervalMs);

		return () => clearInterval(cycle);
	}, [messages, intervalMs]);

	useEffect(() => {
		const dotCycle = setInterval(() => {
			setDots((prev) => (prev + 1) % 4);
		}, 500);
		return () => clearInterval(dotCycle);
	}, []);

	if (messages.length === 0) return null;

	return (
		<div className={cn("min-h-5", ALIGN_CLASS[align])} aria-live="polite">
			<span
				className={cn(
					"inline-flex items-baseline italic transition-opacity duration-[250ms] ease-in-out",
					VARIANT_CLASS[variant],
					color ? undefined : "text-muted-foreground",
					show ? "opacity-100" : "opacity-0",
				)}
				style={color ? { color } : undefined}
			>
				{messages[index]}
				<span className="inline-block w-[1.5em] text-left">
					{".".repeat(dots)}
				</span>
			</span>
		</div>
	);
};

export default RotatingLoadingText;
